// Evacuación: aforo y ancho de egreso (RTQ 5/2026), tiempo de salida de Togawa y RSET.
// Matriz: filas 2.15, 3.1 a 3.5. Togawa y RSET son métodos técnicos aceptados (PE-10, PE-16, PE-17).

import { ESCALERA_MINIMA_RTQ5, FC_RTQ5, FCA_RTQ5, OcupacionFCA, PUERTA_MINIMA_M } from './pe-constantes-normativa';
import { doc, Traza, unirFuentes } from './pe-tipos';
import { formatoTiempo, numEc, segundosAMinutos } from './pe-unidades';

// ── Aforo (RTQ 5, 5.1.1 y Tabla 1) ───────────────────────────────────────────

export function calcularAforo(areaM2: number, ocupacion: OcupacionFCA) {
  if (!(areaM2 > 0)) throw new Error('Área inválida');
  const fila = FCA_RTQ5[ocupacion];
  const fuente = unirFuentes(doc.rtq(5, fila.linea), doc.matriz('59-60'));
  if (fila.fca === null) {
    return {
      aforo: null, aforoPersonas: null,
      trazas: [{
        metodo: 'aforo', norma: 'RTQ 5/2026', articulo_o_tabla: 'Tabla 1', umbral: 'N/A', valor: null,
        unidad: 'personas', estado: 'por_validar', fuente_doc: fuente,
        nota: 'La Tabla 1 no da FCA para almacenamiento: el responsable indica los ocupantes',
      }] as Traza[],
    };
  }
  const aforo = areaM2 / fila.fca;
  // La RTQ 5 no dice cómo redondear: hacia arriba, conservador (PE-34, Kevin 26/10/2026).
  const aforoPersonas = Math.ceil(aforo - 1e-9);
  return {
    aforo, aforoPersonas,
    trazas: [{
      metodo: 'aforo', norma: 'RTQ 5/2026', articulo_o_tabla: '5.1.1 y Tabla 1', umbral: fila.fca, valor: aforo,
      unidad: 'personas', estado: 'informativo', fuente_doc: unirFuentes(fuente, doc.paso0('§7 PE-34')),
      nota: `FCA ${numEc(fila.fca)} m²/persona; redondeo hacia arriba a ${aforoPersonas}`,
    }] as Traza[],
  };
}

// ── Ancho de egreso (RTQ 5, 5.2 y Tabla 2) ───────────────────────────────────

export function anchoEgreso(aforo: number, altoRiesgo = false) {
  const fc = altoRiesgo ? FC_RTQ5.alto_riesgo : FC_RTQ5.general;
  const puertasMm = fc.puertas * aforo;
  const escalerasMm = fc.escaleras * aforo;
  // 5.2.1: si el cálculo es menor que el ancho mínimo de la Tabla 3, se usa el mínimo.
  const puertaRequeridaM = Math.max(puertasMm / 1000, PUERTA_MINIMA_M.valor);
  const trazas: Traza[] = [{
    metodo: 'ancho_egreso', norma: FC_RTQ5.norma, articulo_o_tabla: FC_RTQ5.articulo_o_tabla,
    umbral: `${numEc(fc.puertas)} mm/persona (puertas); ${numEc(fc.escaleras)} mm/persona (escaleras)`, valor: puertasMm,
    unidad: 'mm', estado: 'informativo', fuente_doc: FC_RTQ5.fuente_doc,
    nota: `Puertas: ${numEc(puertasMm, 1)} mm calculados; se exige al menos ${numEc(PUERTA_MINIMA_M.valor)} m (Tablas 3 y 4, 5.2.1)`,
  }];
  return { puertasMm, escalerasMm, puertaRequeridaM, trazas };
}

// Anchos medidos por salida frente a la RTQ 5 (PE-A3b; escaleras en PE-A3c, PE-36; por sector en PE-A3c2).
// Puertas: cada una ≥ 0,86 m (Tablas 3 y 4) y la suma ≥ el ancho calculado (5 mm por persona; 10 en alto riesgo)
// o el mínimo (5.2.1). Escaleras: se agrupan por sector (5.2.2, "la carga de ocupantes de ese piso"): si la
// escalera tiene un sector con ocupantes (emergency_plan_sectors.occupants), el ancho calculado (7,6 o 18 mm por
// persona) y el mínimo de la Tabla 3 (nuevas) o 4 (existentes) salen de esos ocupantes; si no, del aforo total del
// plan, con una traza 'informativo' que lo dice. Cada escalera ≥ el mínimo (5.3) y la suma del grupo ≥ el mayor
// entre el cálculo y el mínimo (5.2.1). Sin saber si la edificación es nueva o existente, las escaleras quedan
// 'por_validar'. Si la RTQ no aplica (fuera de Quito), las trazas quedan 'informativo' citando que no aplica.
export interface SalidaMedida { nombre: string; tipo: 'puerta' | 'escalera'; anchoM: number; sectorId?: string | null }
export type Edificacion = 'nueva' | 'existente';
export interface SectorOcupantes { nombre: string; ocupantes: number | null }

export const NOTA_AFORO_TOTAL_SIN_OCUPANTES =
  'Se usó el aforo total del plan porque el sector no tiene ocupantes; es más exigente que la RTQ 5 numeral 5.2.2';
export const NOTA_AFORO_TOTAL_SIN_SECTOR =
  'Se usó el aforo total del plan porque la escalera no tiene un sector asignado; es más exigente que la RTQ 5 numeral 5.2.2';

export function verificarSalidas(aforo: number, salidas: SalidaMedida[],
  opciones: { altoRiesgo?: boolean; aplicaRtq: boolean; edificacion?: Edificacion | null; sectores?: Record<string, SectorOcupantes> }) {
  const req = anchoEgreso(aforo, opciones.altoRiesgo);
  const fc = opciones.altoRiesgo ? FC_RTQ5.alto_riesgo : FC_RTQ5.general;
  const est = (ok: boolean): Traza['estado'] => (!opciones.aplicaRtq ? 'informativo' : ok ? 'cumple' : 'no_cumple');
  const noAplica = opciones.aplicaRtq ? '' : ' La RTQ 5 solo aplica en el Distrito Metropolitano de Quito: dato de referencia.';
  const ed = opciones.edificacion ?? null;
  const tablaEsc = ed ? ESCALERA_MINIMA_RTQ5[ed] : null;
  const tablaPuerta = ed === 'existente' ? 'Tabla 4' : ed === 'nueva' ? 'Tabla 3' : 'Tablas 3 y 4';
  const trazas: Traza[] = [...req.trazas.map(t => ({ ...t, nota: `${t.nota}${noAplica}` }))];

  // Grupo de cada escalera: su sector si tiene ocupantes; si no, el aforo total.
  const sectorDe = (s: SalidaMedida) => (s.sectorId ? opciones.sectores?.[s.sectorId] : undefined);
  const grupoDe = (s: SalidaMedida) => {
    const sec = sectorDe(s);
    return sec && typeof sec.ocupantes === 'number' && sec.ocupantes >= 0
      ? { clave: `s:${s.sectorId}`, nombre: sec.nombre, n: sec.ocupantes, delSector: true }
      : { clave: 'total', nombre: '', n: aforo, delSector: false };
  };

  for (const s of salidas) {
    if (!(s.anchoM > 0)) throw new Error(`Ancho inválido en "${s.nombre}"`);
    if (s.tipo === 'puerta') {
      trazas.push({
        metodo: 'ancho_egreso', norma: 'RTQ 5/2026', articulo_o_tabla: `${tablaPuerta} · ${s.nombre}`, umbral: PUERTA_MINIMA_M.valor,
        valor: s.anchoM, unidad: 'm', estado: est(s.anchoM >= PUERTA_MINIMA_M.valor), fuente_doc: PUERTA_MINIMA_M.fuente_doc,
        nota: `Ancho mínimo de puerta${noAplica}`,
      });
    } else if (tablaEsc) {
      const g = grupoDe(s);
      const minimo = tablaEsc.minimo(g.n);
      trazas.push({
        metodo: 'ancho_egreso', norma: 'RTQ 5/2026', articulo_o_tabla: `${tablaEsc.tabla} · ${s.nombre}`, umbral: minimo,
        valor: s.anchoM, unidad: 'm', estado: est(s.anchoM + 1e-9 >= minimo), fuente_doc: tablaEsc.fuente_doc,
        nota: `Ancho mínimo de escalera, edificación ${ed}: ${tablaEsc.corte(g.n)}`
          + `${g.delSector ? ` (ocupantes del sector ${g.nombre})` : ' (aforo total del plan)'} (5.3)${noAplica}`,
      });
    }
  }
  const puertas = salidas.filter(s => s.tipo === 'puerta');
  if (puertas.length) {
    const sumaM = puertas.reduce((a, s) => a + s.anchoM, 0);
    trazas.push({
      metodo: 'ancho_egreso', norma: FC_RTQ5.norma, articulo_o_tabla: '5.2 · suma de puertas', umbral: req.puertaRequeridaM, valor: sumaM,
      unidad: 'm', estado: est(sumaM + 1e-9 >= req.puertaRequeridaM), fuente_doc: FC_RTQ5.fuente_doc,
      nota: `${puertas.length} puertas medidas para ${aforo} personas${noAplica}`,
    });
  }
  const escaleras = salidas.filter(s => s.tipo === 'escalera');
  let escaleraRequeridaM: number | null = null;
  const requeridaPorGrupo: Record<string, number> = {};
  if (escaleras.length && !tablaEsc) {
    trazas.push({
      metodo: 'ancho_egreso', norma: 'RTQ 5/2026', articulo_o_tabla: 'Tablas 3 y 4 · escaleras', umbral: null, valor: null, unidad: 'm',
      estado: opciones.aplicaRtq ? 'por_validar' : 'informativo', fuente_doc: PUERTA_MINIMA_M.fuente_doc, paso: 1,
      nota: `Falta indicar si la edificación es nueva o existente: el ancho mínimo de escaleras depende de eso (Tabla 3 o 4)${noAplica}`,
    });
  } else if (escaleras.length && tablaEsc) {
    const grupos = new Map<string, { g: ReturnType<typeof grupoDe>; esc: SalidaMedida[] }>();
    for (const s of escaleras) {
      const g = grupoDe(s);
      if (!grupos.has(g.clave)) grupos.set(g.clave, { g, esc: [] });
      grupos.get(g.clave)!.esc.push(s);
    }
    for (const { g, esc } of Array.from(grupos.values())) {
      const minimo = tablaEsc.minimo(g.n);
      const calculadoMm = fc.escaleras * g.n;
      const calculadoM = calculadoMm / 1000;
      const requeridaM = Math.max(calculadoM, minimo);
      requeridaPorGrupo[g.clave] = requeridaM;
      if (!g.delSector) escaleraRequeridaM = requeridaM;
      const sumaM = esc.reduce((a, s) => a + s.anchoM, 0);
      const sufijo = g.delSector ? ` · ${g.nombre}` : '';
      trazas.push({
        metodo: 'ancho_egreso', norma: FC_RTQ5.norma, articulo_o_tabla: `5.2 · suma de escaleras${sufijo}`, umbral: requeridaM, valor: sumaM,
        unidad: 'm', estado: est(sumaM + 1e-9 >= requeridaM), fuente_doc: unirFuentes(FC_RTQ5.fuente_doc, tablaEsc.fuente_doc),
        nota: `${esc.length} escaleras medidas para ${g.n} personas; calculado ${formatoMm(calculadoMm)} mm`
          + `${calculadoM < minimo ? `, menor que el mínimo de la ${tablaEsc.tabla} (${numEc(minimo)} m): se usa el mínimo (5.2.1)` : ''}${noAplica}`,
      });
      // 5.2.2 (PE-B1, 6b): una traza por caso, con su texto: escaleras sin sector o de un sector sin ocupantes.
      const casos = g.delSector ? [{ art: `5.2.2 · ${g.nombre}`, nota: `Escaleras del sector ${g.nombre} calculadas con sus ${g.n} ocupantes (5.2.2)`, esc }]
        : [{ art: '5.2.2 · ocupantes por piso', nota: NOTA_AFORO_TOTAL_SIN_SECTOR, esc: esc.filter(s => !s.sectorId || !opciones.sectores?.[s.sectorId]) },
          { art: '5.2.2 · sector sin ocupantes', nota: NOTA_AFORO_TOTAL_SIN_OCUPANTES, esc: esc.filter(s => s.sectorId && opciones.sectores?.[s.sectorId]) }]
          .filter(c => c.esc.length > 0);
      for (const c of casos) {
        trazas.push({
          metodo: 'ancho_egreso', norma: 'RTQ 5/2026', articulo_o_tabla: c.art, umbral: null, valor: g.n, unidad: 'personas', estado: 'informativo',
          fuente_doc: ESCALERA_MINIMA_RTQ5.pisos_fuente_doc, nota: `${c.nota} (${c.esc.map(x => x.nombre).join(', ')})`,
        });
      }
    }
  }
  return { ...req, escaleraRequeridaM, requeridaPorGrupo, trazas };
}

const formatoMm = (mm: number) => String(Math.round(mm * 10) / 10).replace('.', ',');

// ── Togawa (PE-17, plan de referencia §14.1; PASO0 §3.3.3) ───────────────────

export const TOGAWA_POR_DEFECTO = {
  k: 1.3, // personas/(m·s)
  v: 0.6, // m/s
  fuente_doc: doc.paso0('§3.3.3 y §7 PE-17'),
};

export interface EntradaTogawa {
  n: number;  // personas
  a: number;  // ancho de salida (m)
  d: number;  // distancia total (m)
  k?: number; // editable por el responsable
  v?: number; // editable por el responsable
}

export function togawa(e: EntradaTogawa) {
  const k = e.k ?? TOGAWA_POR_DEFECTO.k;
  const v = e.v ?? TOGAWA_POR_DEFECTO.v;
  if (!(e.a > 0) || !(k > 0) || !(v > 0) || e.n < 0 || e.d < 0) throw new Error('Datos de Togawa inválidos');
  const tsSegundos = e.n / (e.a * k) + e.d / v;
  const tsMinutos = segundosAMinutos(tsSegundos);
  const trazas: Traza[] = [{
    metodo: 'togawa', norma: 'Togawa (método técnico aceptado)', articulo_o_tabla: 'TS = N/(A·K) + D/V',
    umbral: null, valor: tsSegundos, unidad: 's', estado: 'informativo', fuente_doc: TOGAWA_POR_DEFECTO.fuente_doc,
    nota: `TS = ${formatoTiempo(tsSegundos)}; K = ${numEc(k)} personas/(m·s), V = ${numEc(v)} m/s${e.k !== undefined || e.v !== undefined ? ' (editados por el responsable)' : ' (por defecto)'}`,
  }];
  return { tsSegundos, tsMinutos, k, v, trazas };
}

// ── RSET (PE-16, PE-23) ──────────────────────────────────────────────────────
// El PDF muestra Togawa y el RSET por separado. El premovimiento no tiene valor por defecto: lo ingresa
// el responsable y se ajusta con los simulacros; sin él, solo Togawa (PE-23, Kevin 26/10/2026).

export interface EntradaRset {
  deteccionS: number;
  alarmaS: number;
  premovimientoS?: number; // sin valor por defecto (PE-23)
  togawaS: number;
}

export function rset(e: EntradaRset) {
  if (e.premovimientoS === undefined) {
    return {
      rsetSegundos: null, rsetMinutos: null, togawaS: e.togawaS,
      trazas: [{
        metodo: 'rset', norma: 'RSET (método técnico aceptado)', articulo_o_tabla: 'detección + alarma + premovimiento + TS',
        umbral: null, valor: null, unidad: 's', estado: 'por_validar', fuente_doc: unirFuentes(doc.matriz(53), doc.paso0('§7 PE-23')),
        nota: 'Falta el tiempo de premovimiento: el RSET no se calcula y se muestra solo el tiempo de Togawa', paso: 3,
      }] as Traza[],
    };
  }
  const rsetSegundos = e.deteccionS + e.alarmaS + e.premovimientoS + e.togawaS;
  return {
    rsetSegundos, rsetMinutos: segundosAMinutos(rsetSegundos), togawaS: e.togawaS,
    trazas: [{
      metodo: 'rset', norma: 'RSET (método técnico aceptado)', articulo_o_tabla: 'detección + alarma + premovimiento + TS',
      umbral: null, valor: rsetSegundos, unidad: 's', estado: 'informativo', fuente_doc: doc.matriz(53),
      nota: `RSET = ${formatoTiempo(rsetSegundos)}; premovimiento ${numEc(e.premovimientoS, 1)} s definido por el responsable`,
    }] as Traza[],
  };
}
